import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import PaymentMethodCard from './components/PaymentMethodCard';
import UsageTrendChart from './components/UsageTrendChart';
import PerformanceRankingTable from './components/PerformanceRankingTable';
import ComparisonMatrix from './components/ComparisonMatrix';
import FilterPanel from './components/FilterPanel';
import ExportPanel from './components/ExportPanel';

const PaymentMethodPerformance = () => {
  const [selectedMethods, setSelectedMethods] = useState([
    'VA Transfer',
    'QRIS',
    'OVO',
    'DANA',
    'GoPay',
    'Card'
  ]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [processingStatus, setProcessingStatus] = useState('updated');
  const [filters, setFilters] = useState({
    region: 'all',
    period: 'last7days',
    amountRange: 'all',
    merchantCategory: 'all',
    timeSlot: 'all'
  });

  const paymentMethods = [
    {
      name: 'VA Transfer',
      category: 'Bank Transfer',
      successRate: 96.8,
      successTrend: 2.3,
      avgTransaction: 850000,
      totalTransactions: 45678,
      adoptionRate: 34.2,
      adoptionTrend: 5.1,
      revenueContribution: 38.5
    },
    {
      name: 'QRIS',
      category: 'QR Payment',
      successRate: 94.2,
      successTrend: 3.7,
      avgTransaction: 125000,
      totalTransactions: 67890,
      adoptionRate: 28.6,
      adoptionTrend: 8.4,
      revenueContribution: 22.3
    },
    {
      name: 'OVO',
      category: 'E-Wallet',
      successRate: 92.5,
      successTrend: -1.2,
      avgTransaction: 95000,
      totalTransactions: 34567,
      adoptionRate: 15.8,
      adoptionTrend: -2.3,
      revenueContribution: 12.7
    },
    {
      name: 'DANA',
      category: 'E-Wallet',
      successRate: 91.8,
      successTrend: 1.8,
      avgTransaction: 87000,
      totalTransactions: 28901,
      adoptionRate: 12.4,
      adoptionTrend: 3.2,
      revenueContribution: 9.8
    },
    {
      name: 'GoPay',
      category: 'E-Wallet',
      successRate: 93.1,
      successTrend: 2.1,
      avgTransaction: 92000,
      totalTransactions: 31234,
      adoptionRate: 13.7,
      adoptionTrend: 4.5,
      revenueContribution: 11.2
    },
    {
      name: 'Card',
      category: 'Credit/Debit Card',
      successRate: 89.4,
      successTrend: -0.8,
      avgTransaction: 1250000,
      totalTransactions: 12345,
      adoptionRate: 8.9,
      adoptionTrend: -1.5,
      revenueContribution: 18.9
    }
  ];

  const trendData = [
    {
      date: '28 Jan',
      'VA Transfer': 12500,
      QRIS: 18900,
      OVO: 9800,
      DANA: 8200,
      GoPay: 8900,
      Card: 3500
    },
    {
      date: '29 Jan',
      'VA Transfer': 13200,
      QRIS: 19500,
      OVO: 9500,
      DANA: 8400,
      GoPay: 9100,
      Card: 3600
    },
    {
      date: '30 Jan',
      'VA Transfer': 14100,
      QRIS: 20800,
      OVO: 10200,
      DANA: 8900,
      GoPay: 9600,
      Card: 3800
    },
    {
      date: '31 Jan',
      'VA Transfer': 13800,
      QRIS: 21200,
      OVO: 9900,
      DANA: 8700,
      GoPay: 9400,
      Card: 3700
    },
    {
      date: '01 Feb',
      'VA Transfer': 15200,
      QRIS: 22500,
      OVO: 11100,
      DANA: 9500,
      GoPay: 10200,
      Card: 4100
    },
    {
      date: '02 Feb',
      'VA Transfer': 14900,
      QRIS: 23100,
      OVO: 10800,
      DANA: 9300,
      GoPay: 9900,
      Card: 3900
    },
    {
      date: '03 Feb',
      'VA Transfer': 15600,
      QRIS: 24300,
      OVO: 11500,
      DANA: 9800,
      GoPay: 10500,
      Card: 4200
    }
  ];

  const rankingData = [
    {
      rank: 1,
      method: 'VA Transfer',
      score: 96.8,
      transactions: 45678,
      revenue: 38.5
    },
    {
      rank: 2,
      method: 'QRIS',
      score: 94.2,
      transactions: 67890,
      revenue: 22.3
    },
    {
      rank: 3,
      method: 'GoPay',
      score: 93.1,
      transactions: 31234,
      revenue: 11.2
    },
    {
      rank: 4,
      method: 'OVO',
      score: 92.5,
      transactions: 34567,
      revenue: 12.7
    },
    {
      rank: 5,
      method: 'DANA',
      score: 91.8,
      transactions: 28901,
      revenue: 9.8
    },
    {
      rank: 6,
      method: 'Card',
      score: 89.4,
      transactions: 12345,
      revenue: 18.9
    }
  ];

  const comparisonData = [
    {
      name: 'VA Transfer',
      successRate: 96.8,
      failureReason: 'Insufficient Balance',
      settlementTime: 2,
      userPreference: 4.5
    },
    {
      name: 'QRIS',
      successRate: 94.2,
      failureReason: 'QR Code Expired',
      settlementTime: 1,
      userPreference: 4.7
    },
    {
      name: 'OVO',
      successRate: 92.5,
      failureReason: 'Account Verification',
      settlementTime: 0.5,
      userPreference: 4.2
    },
    {
      name: 'DANA',
      successRate: 91.8,
      failureReason: 'Daily Limit Exceeded',
      settlementTime: 0.5,
      userPreference: 4.1
    },
    {
      name: 'GoPay',
      successRate: 93.1,
      failureReason: 'Network Timeout',
      settlementTime: 0.5,
      userPreference: 4.3
    },
    {
      name: 'Card',
      successRate: 89.4,
      failureReason: 'Card Declined',
      settlementTime: 24,
      userPreference: 3.8
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setProcessingStatus('processing');
      setTimeout(() => {
        setLastUpdate(new Date());
        setProcessingStatus('updated');
      }, 2000);
    }, 600000);

    return () => clearInterval(interval);
  }, []);

  const handleMethodToggle = (methodName) => {
    setSelectedMethods((prev) =>
      prev?.includes(methodName)
        ? prev?.filter((m) => m !== methodName)
        : [...prev, methodName]
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      region: 'all',
      period: 'last7days',
      amountRange: 'all',
      merchantCategory: 'all',
      timeSlot: 'all'
    });
  };

  const handleExport = async (format) => {
    console.log(`Exporting data in ${format} format...`);
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Export completed');
        resolve();
      }, 2000);
    });
  };

  const formatLastUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);

    if (diff < 60) return `${diff} detik yang lalu`;
    if (diff < 3600) return `${Math.floor(diff / 60)} menit yang lalu`;
    return lastUpdate?.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="content-container">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4 mb-3 md:mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                  Payment Method Performance
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1 md:mt-2">
                  Comprehensive analysis of payment channel effectiveness
                </p>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-lg ${
                    processingStatus === 'processing' ?'bg-warning/10 text-warning' :'bg-success/10 text-success'
                  }`}
                >
                  <Icon
                    name={processingStatus === 'processing' ? 'RefreshCw' : 'CheckCircle2'}
                    size={16}
                    className={processingStatus === 'processing' ? 'animate-spin' : ''}
                  />
                  <span className="text-xs md:text-sm font-medium">
                    {processingStatus === 'processing' ? 'Processing...' : 'Updated'}
                  </span>
                </div>
                <div className="text-xs md:text-sm text-muted-foreground">
                  {formatLastUpdate()}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            {paymentMethods?.map((method) => (
              <PaymentMethodCard
                key={method?.name}
                method={method}
                isSelected={selectedMethods?.includes(method?.name)}
                onToggle={() => handleMethodToggle(method?.name)}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-8">
              <UsageTrendChart
                data={trendData}
                selectedMethods={selectedMethods}
                onMethodToggle={handleMethodToggle}
              />
            </div>
            <div className="lg:col-span-4">
              <PerformanceRankingTable rankings={rankingData} />
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <ComparisonMatrix comparisonData={comparisonData} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
              />
            </div>
            <div className="lg:col-span-1">
              <ExportPanel onExport={handleExport} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentMethodPerformance;